import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCopy, FaInfoCircle, FaPaperPlane, FaPlus, FaSave } from "react-icons/fa";
import { Button } from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { PageLayout } from "../../components/layout";
import { useIsKioskOwner } from "../../hooks/useCurrentRole";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import {
  deleteSocialMediaCampaignMessage,
  getSocialMediaCampaignMessages,
  publishSocialMediaCampaignMessage,
  recordMessageCopy,
  saveSocialMediaCampaignDraft,
  unpublishSocialMediaCampaignMessage,
} from "../../services/SocialMediaCampaignService";
import { SocialMediaCampaignMessage, SocialMediaCampaignStatus, SOCIAL_MEDIA_TEMPLATE_PARAMS } from "../../types/socialMediaCampaign.types";
import "./SocialMediaCampaignPage.css";

const formatDate = (value?: string | null) => {
  if (!value) {
    return "Not published";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Not published";
  }

  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getTemplateText = (message: SocialMediaCampaignMessage | null) =>
  message?.templateContent || message?.plainContent || "";

const getStatusOrder = (status: SocialMediaCampaignStatus) => {
  const order: Record<SocialMediaCampaignStatus, number> = {
    PUBLISHED: 0,
    DRAFT: 1,
    ARCHIVED: 2,
  };
  return order[status] ?? 3;
};

const countPlaceholders = (value: string) => {
  const matches = value.match(/\{\{\w+\}\}/g);
  return matches ? matches.length : 0;
};

const asMessageSnippet = (value: string) => {
  if (!value.trim()) {
    return "No message body";
  }
  return value.length > 130 ? `${value.slice(0, 130)}...` : value;
};

const NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

const CURRENCY_FORMATTER = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DEFAULT_TEMPLATE_SAMPLE_PARAMS: Record<string, string | number> = {
  kiosk_name: "Downtown Kiosk",
  kiosk_meals_provided: 1234,
  kiosk_amount_raised: 3456.78,
  global_food_bars: 98765,
};

const parseNumericValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const sanitized = value.replace(/[^0-9.-]/g, "");
    if (!sanitized) {
      return null;
    }

    const parsed = Number(sanitized);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const formatTemplateParamValue = (key: string, value: unknown): string => {
  const numericValue = parseNumericValue(value);
  const keyName = key.toLowerCase();
  const isCurrencyParam = keyName.includes("amount") || keyName.includes("raised") || keyName.includes("currency");

  if (isCurrencyParam && numericValue !== null) {
    return CURRENCY_FORMATTER.format(numericValue);
  }

  if (numericValue !== null && keyName !== "kiosk_name") {
    return NUMBER_FORMATTER.format(numericValue);
  }

  if (typeof value === "string") {
    return value;
  }

  return String(value ?? "");
};

const resolveTemplateText = (
  template: string,
  params?: Record<string, unknown> | null,
) => template.replace(/\{\{(\w+)\}\}/g, (_match, key) => {
  const rawValue = params?.[key];
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return `{{${key}}}`;
  }
  return formatTemplateParamValue(key, rawValue);
});

const getResolvedMessageContent = (
  message: SocialMediaCampaignMessage,
  fallbackParams?: Record<string, unknown>,
) => {
  const template = getTemplateText(message);
  if (!template) {
    return message.resolvedContent || message.plainContent || "";
  }

  const params = (message.templateParams as Record<string, unknown> | null) ?? fallbackParams ?? null;
  if (!params) {
    return message.resolvedContent || template;
  }

  return resolveTemplateText(template, params);
};

function AdminView() {
  const [messages, setMessages] = useState<SocialMediaCampaignMessage[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | SocialMediaCampaignStatus>("ALL");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    type: "delete" | "publish" | "unpublish";
    messageId: number | null;
  }>({
    open: false,
    type: "delete",
    messageId: null,
  });
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const editingMessage = useMemo(
    () => messages.find((message) => message.id === editingId) ?? null,
    [messages, editingId],
  );

  const filteredMessages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return messages
      .filter((message) => (statusFilter === "ALL" ? true : message.status === statusFilter))
      .filter((message) => {
        if (!query) {
          return true;
        }
        return (`${message.title} ${getTemplateText(message)}`).toLowerCase().includes(query);
      })
      .sort((a, b) => {
        const statusRank = getStatusOrder(a.status) - getStatusOrder(b.status);
        if (statusRank !== 0) {
          return statusRank;
        }
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [messages, searchTerm, statusFilter]);

  const statusCounts = useMemo(() => ({
    all: messages.length,
    published: messages.filter((message) => message.status === "PUBLISHED").length,
    draft: messages.filter((message) => message.status === "DRAFT").length,
    archived: messages.filter((message) => message.status === "ARCHIVED").length,
  }), [messages]);

  const placeholderCount = useMemo(() => countPlaceholders(templateContent), [templateContent]);
  const characterCount = useMemo(() => templateContent.length, [templateContent]);
  const isPublishedEditing = editingMessage?.status === "PUBLISHED";
  const isDirty = useMemo(() => {
    if (!editingMessage) {
      return Boolean(title.trim() || templateContent.trim());
    }
    return title !== editingMessage.title || templateContent !== getTemplateText(editingMessage);
  }, [editingMessage, title, templateContent]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getSocialMediaCampaignMessages();
      const nextMessages = result.messages ?? [];
      setMessages(nextMessages);

      if (nextMessages.length > 0) {
        return;
      }
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to load campaign messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isEditorOpen) {
      return;
    }

    if (editingMessage) {
      setTitle(editingMessage.title);
      setTemplateContent(getTemplateText(editingMessage));
    }
  }, [editingMessage, isEditorOpen]);

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingId(null);
    setTitle("");
    setTemplateContent("");
  };

  const openCreateEditor = () => {
    setEditingId(null);
    setTitle("");
    setTemplateContent("");
    setIsEditorOpen(true);
  };

  const openEditEditor = (message: SocialMediaCampaignMessage) => {
    if (message.status === "PUBLISHED") {
      showErrorToast("This message is published. Unpublish it before editing.");
      return;
    }

    setEditingId(message.id);
    setTitle(message.title);
    setTemplateContent(getTemplateText(message));
    setIsEditorOpen(true);
  };

  const insertParam = (key: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const placeholder = `{{${key}}}`;
    const next = templateContent.slice(0, start) + placeholder + templateContent.slice(end);
    setTemplateContent(next);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  };

  const validate = () => {
    if (!title.trim()) {
      throw new Error("A title is required.");
    }
    if (!templateContent.trim()) {
      throw new Error("A message template is required.");
    }
  };

  const saveDraft = async () => {
    if (editingMessage && editingMessage.status === "PUBLISHED") {
      showErrorToast("This message is published. Unpublish it before editing.");
      return;
    }

    setSaving(true);
    try {
      validate();
      const payload = {
        id: editingMessage?.id,
        title: title.trim(),
        templateContent: templateContent.trim(),
        plainContent: templateContent.trim(),
        contentHtml: `<p>${templateContent.trim().replace(/\n/g, "<br />")}</p>`,
      };

      const saved = await saveSocialMediaCampaignDraft(payload);
      setMessages((current) => {
        const nextList = current.filter((message) => message.id !== saved.id);
        return [saved, ...nextList];
      });
      setEditingId(saved.id);
      showSuccessToast("Draft saved.");
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const publishSelectedMessage = async () => {
    if (editingMessage && editingMessage.status === "PUBLISHED") {
      showErrorToast("This message is published. Unpublish it before editing and republishing.");
      return;
    }

    setPublishing(true);
    try {
      validate();
      const payload = {
        id: editingMessage?.id,
        title: title.trim(),
        templateContent: templateContent.trim(),
        plainContent: templateContent.trim(),
        contentHtml: `<p>${templateContent.trim().replace(/\n/g, "<br />")}</p>`,
      };

      const saved = await saveSocialMediaCampaignDraft(payload);
      const result = await publishSocialMediaCampaignMessage(saved.id);

      setMessages((current) => {
        const withoutSelected = current.filter((message) => message.id !== result.message.id);
        return [result.message, ...withoutSelected];
      });

      showSuccessToast(`Published. Notification sent to ${result.notificationRecipient}.`);
      await load();
      closeEditor();
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to publish.");
    } finally {
      setPublishing(false);
    }
  };

  const closeConfirmation = () => {
    setConfirmState({ open: false, type: "delete", messageId: null });
  };

  const getConfirmationConfig = () => {
    switch (confirmState.type) {
      case "delete":
        return {
          title: "Delete message?",
          message: "This will permanently remove the message. This action cannot be undone.",
          confirmLabel: "Delete",
          confirmVariant: "primary" as const,
        };
      case "publish":
        return {
          title: "Publish message?",
          message: "This message will become visible to kiosk owners and ready to share.",
          confirmLabel: "Publish",
          confirmVariant: "primary" as const,
        };
      case "unpublish":
        return {
          title: "Unpublish message?",
          message: "This message will no longer be visible to kiosk owners.",
          confirmLabel: "Unpublish",
          confirmVariant: "primary" as const,
        };
      default:
        return {
          title: "Confirm action",
          message: "Are you sure you want to continue?",
          confirmLabel: "Confirm",
          confirmVariant: "primary" as const,
        };
    }
  };

  const handleConfirmAction = async () => {
    if (confirmState.messageId === null) {
      closeConfirmation();
      return;
    }

    const messageId = confirmState.messageId;
    setActioningId(messageId);
    setPublishing(confirmState.type !== "delete");

    try {
      if (confirmState.type === "delete") {
        await deleteSocialMediaCampaignMessage(messageId);
        setMessages((current) => current.filter((message) => message.id !== messageId));
        showSuccessToast("Message deleted.");
      } else if (confirmState.type === "publish") {
        const updatedMessage = await publishSocialMediaCampaignMessage(messageId);
        setMessages((current) =>
          current.map((item) => (item.id === messageId ? updatedMessage.message : item)),
        );
        showSuccessToast("Message published.");
      } else {
        const updatedMessage = await unpublishSocialMediaCampaignMessage(messageId);
        setMessages((current) =>
          current.map((item) => (item.id === messageId ? updatedMessage.message : item)),
        );
        showSuccessToast("Message unpublished.");
      }
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to update message status.");
    } finally {
      setActioningId(null);
      setPublishing(false);
      closeConfirmation();
    }
  };

  const openDeleteConfirmation = (messageId: number) => {
    setConfirmState({ open: true, type: "delete", messageId });
  };

  const openPublishConfirmation = (messageId: number) => {
    setConfirmState({ open: true, type: "publish", messageId });
  };

  const openUnpublishConfirmation = (messageId: number) => {
    setConfirmState({ open: true, type: "unpublish", messageId });
  };

  const previewContent = resolveTemplateText(templateContent, DEFAULT_TEMPLATE_SAMPLE_PARAMS);

  if (loading) {
    return (
      <PageLayout title="Social Media">
        <div className="smc-loading">Loading…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Social Media" subtitle="Create and manage your social message library.">
      <div className="smc-page">
        <section className="smc-card smc-dashboard-panel">
          <div className="smc-card-header smc-card-header--stacked smc-dashboard-header">
            <div>
              <p className="smc-eyebrow">Messages</p>
              <h2>Library</h2>
            </div>
            <Button variant="primary" size="sm" startIcon={<FaPlus />} onClick={() => {
              openCreateEditor();
            }}>
              New
            </Button>
          </div>

          <div className="smc-insights-grid">
            <div className="smc-insight-card">
              <span>Total</span>
              <strong>{statusCounts.all}</strong>
            </div>
            <div className="smc-insight-card">
              <span>Published</span>
              <strong>{statusCounts.published}</strong>
            </div>
            <div className="smc-insight-card">
              <span>Drafts</span>
              <strong>{statusCounts.draft}</strong>
            </div>
            <div className="smc-insight-card">
              <span>Archived</span>
              <strong>{statusCounts.archived}</strong>
            </div>
          </div>

          <div className="smc-toolbar smc-toolbar--dashboard">
            <input
              className="smc-list-search"
              type="search"
              value={searchTerm}
              placeholder="Search by title or message"
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search social media messages"
            />
            <div className="smc-filter-chips" role="tablist" aria-label="Message status filters">
              {(["ALL", "PUBLISHED", "DRAFT", "ARCHIVED"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === value}
                  className={`smc-filter-chip ${statusFilter === value ? "is-active" : ""}`}
                  onClick={() => setStatusFilter(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {filteredMessages.length === 0 ? (
            <div className="smc-empty-stack">
              <p>{messages.length === 0 ? "No messages yet." : "No messages match your filters."}</p>
            </div>
          ) : (
            <div className="smc-message-grid">
              {filteredMessages.map((message) => {
                const cardPreview = getResolvedMessageContent(message, DEFAULT_TEMPLATE_SAMPLE_PARAMS);

                return (
                  <article
                    key={message.id}
                    className="smc-message-card"
                  >
                    <div className="smc-message-list-top">
                      <span className={`smc-status smc-status--${message.status.toLowerCase()}`}>{message.status}</span>
                      <span className="smc-message-date">{formatDate(message.updatedAt)}</span>
                    </div>
                    <h3>{message.title}</h3>
                    <p>{asMessageSnippet(getTemplateText(message))}</p>
                    <div className="smc-message-preview-box">
                      <pre className="smc-preview-content">{cardPreview || "No message body"}</pre>
                    </div>
                    <div className="smc-message-list-actions">
                      <button
                        type="button"
                        className="smc-action-link"
                        disabled={message.status === "PUBLISHED" || publishing || actioningId === message.id}
                        onClick={() => {
                          void openEditEditor(message);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="smc-action-link"
                        disabled={publishing || actioningId === message.id}
                        onClick={() => {
                          if (message.status === "PUBLISHED") {
                            openUnpublishConfirmation(message.id);
                            return;
                          }
                          openPublishConfirmation(message.id);
                        }}
                      >
                        {message.status === "PUBLISHED" ? "Unpublish" : "Publish"}
                      </button>
                      <button
                        type="button"
                        className="smc-action-link smc-action-link--danger"
                        disabled={publishing || actioningId === message.id}
                        onClick={() => {
                          openDeleteConfirmation(message.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <ConfirmModal
        open={confirmState.open}
        onClose={closeConfirmation}
        onConfirm={() => {
          void handleConfirmAction();
        }}
        title={getConfirmationConfig().title}
        message={getConfirmationConfig().message}
        confirmLabel={getConfirmationConfig().confirmLabel}
        confirmVariant={getConfirmationConfig().confirmVariant}
      />

      {isEditorOpen && createPortal(
        <div
          className="smc-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!saving && !publishing) {
              closeEditor();
            }
          }}
        >
          <section
            className="smc-modal"
            role="dialog"
            aria-modal="true"
            aria-label={editingMessage ? "Edit social message" : "Create social message"}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="smc-card-header">
              <h2>{editingMessage ? "Edit message" : "Create message"}</h2>
              <div className="smc-editor-meta">
                {isDirty && <span className="smc-dirty-pill">Unsaved changes</span>}
                {editingMessage && (
                  <span className={`smc-status smc-status--${editingMessage.status.toLowerCase()}`}>
                    {editingMessage.status}
                  </span>
                )}
              </div>
            </div>

            <div className="smc-editor-grid">
              <div className="smc-editor-main">
                <label className="smc-field">
                  <span>Title</span>
                  <input
                    className="smc-input"
                    type="text"
                    placeholder="e.g. July Impact Update"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    disabled={isPublishedEditing}
                    readOnly={isPublishedEditing}
                  />
                </label>

                <label className="smc-field">
                  <span>Message</span>
                  <span className="smc-field-hint">Use the parameter buttons on the right to insert live data placeholders.</span>
                  <textarea
                    ref={textareaRef}
                    className="smc-textarea"
                    rows={10}
                    placeholder="Write your message here…"
                    value={templateContent}
                    onChange={(event) => setTemplateContent(event.target.value)}
                    disabled={isPublishedEditing}
                    readOnly={isPublishedEditing}
                  />
                </label>

                <div className="smc-actions">
                  <Button
                    variant="tertiary"
                    onClick={closeEditor}
                    disabled={saving || publishing}
                  >
                    Cancel
                  </Button>
                  <Button variant="secondary" startIcon={<FaSave />} onClick={() => void saveDraft()} disabled={saving || publishing || isPublishedEditing}>
                    {saving ? "Saving…" : "Save Draft"}
                  </Button>
                  <Button variant="primary" startIcon={<FaPaperPlane />} onClick={() => void publishSelectedMessage()} disabled={saving || publishing || isPublishedEditing}>
                    {publishing ? "Publishing…" : editingMessage ? "Publish message" : "Publish & notify"}
                  </Button>
                </div>
                <div className="smc-composer-metrics">
                  <span>{characterCount} characters</span>
                  <span>{placeholderCount} placeholders</span>
                </div>
              </div>

              <div className="smc-sidebar smc-sidebar--inline">
                <section className="smc-card smc-card--nested">
                  <div className="smc-card-header">
                    <h3>Parameters</h3>
                    <FaInfoCircle className="smc-info-icon" title="Click a parameter to insert it at the cursor." />
                  </div>
                  <p className="smc-param-hint">Click to insert at cursor position.</p>
                  <div className="smc-param-list">
                    {SOCIAL_MEDIA_TEMPLATE_PARAMS.map((param) => (
                      <button
                        key={param.key}
                        type="button"
                        className="smc-param-chip"
                        onClick={() => insertParam(param.key)}
                        title={param.description}
                      >
                        <code>{`{{${param.key}}}`}</code>
                        <span>{param.label}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="smc-card smc-card--nested">
                  <div className="smc-card-header">
                    <h3>Preview</h3>
                    <span className="smc-preview-badge">Sample values</span>
                  </div>
                  {title && <p className="smc-preview-title">{title}</p>}
                  <pre className="smc-preview-content">{previewContent || "Your message will appear here…"}</pre>
                </section>
              </div>
            </div>
          </section>
        </div>,
        document.body,
      )}
    </PageLayout>
  );
}

function OwnerView() {
  const [messages, setMessages] = useState<SocialMediaCampaignMessage[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [copyingId, setCopyingId] = useState<number | null>(null);

  const filteredMessages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return messages;
    }
    return messages.filter((message) =>
      (`${message.title} ${message.resolvedContent || getTemplateText(message)}`).toLowerCase().includes(query),
    );
  }, [messages, searchTerm]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getSocialMediaCampaignMessages();
      const publishedMessages = (result.messages ?? []).filter((message) => message.status === "PUBLISHED");
      setMessages(publishedMessages);
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to load messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const copyMessage = async (message: SocialMediaCampaignMessage) => {
    const textToCopy = getResolvedMessageContent(message);
    if (!textToCopy) {
      showErrorToast("No message available to copy.");
      return;
    }

    setCopyingId(message.id);
    try {
      await navigator.clipboard.writeText(textToCopy);
      await recordMessageCopy(message.id, textToCopy, message.templateParams ?? null);
      showSuccessToast("Message copied. Paste it into your social channel.");
    } catch {
      showErrorToast("Unable to copy automatically. Please select and copy the text manually.");
    } finally {
      setCopyingId(null);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Social Media">
        <div className="smc-loading">Loading…</div>
      </PageLayout>
    );
  }

  if (!messages.length) {
    return (
      <PageLayout title="Social Media" subtitle="Ready-to-share messages from Track Your Impact.">
        <div className="smc-page">
          <div className="smc-empty">
            <h2>No published message yet</h2>
            <p>A new campaign message will appear here as soon as it is published by the admin team.</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Social Media" subtitle="Choose a message and copy the final text ready to share.">
      <div className="smc-page">
        <section className="smc-card smc-dashboard-panel">
          <div className="smc-card-header smc-card-header--stacked smc-dashboard-header">
            <div>
              <p className="smc-eyebrow">Messages</p>
              <h2>Library</h2>
            </div>
          </div>

          <div className="smc-toolbar smc-toolbar--dashboard">
            <input
              className="smc-list-search"
              type="search"
              value={searchTerm}
              placeholder="Search published messages"
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search published social media messages"
            />
          </div>

          {filteredMessages.length === 0 ? (
            <div className="smc-empty-stack">
              <p>No published messages match your search.</p>
            </div>
          ) : (
            <div className="smc-message-grid">
              {filteredMessages.map((message) => {
                const resolvedContent = getResolvedMessageContent(message);

                return (
                  <article
                    key={message.id}
                    className="smc-message-card"
                  >
                <div className="smc-message-list-top smc-message-list-top--owner">
                  <span className="smc-message-date">{formatDate(message.publishedAt)}</span>
                </div>
                <h3>{message.title}</h3>
                <p>{asMessageSnippet(resolvedContent)}</p>

                <div className="smc-message-preview-box">
                  <pre className="smc-preview-content">{resolvedContent || "No message body"}</pre>
                </div>

                <div className="smc-message-list-actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    startIcon={<FaCopy />}
                    onClick={() => {
                      void copyMessage(message);
                    }}
                    disabled={copyingId === message.id}
                  >
                    {copyingId === message.id ? "Copying..." : "Copy message"}
                  </Button>
                </div>
              </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </PageLayout>
  );
}

export const SocialMediaCampaignPage = () => {
  const isKioskOwner = useIsKioskOwner();
  return isKioskOwner ? <OwnerView /> : <AdminView />;
};

export default SocialMediaCampaignPage;
